import { choice, exercise, section, source } from './helpers.ts';

export const foundations = [
  exercise({
    id: 'foundations-biology',
    category: 'foundations',
    title: 'Cells, boundaries, and energy',
    topic: 'Biology · Middle school to early high school',
    difficulty: 'Challenging',
    reading: {
      title: 'Cells, transport, and energy',
      sources: [
        source(
          'OpenStax · Concepts of Biology, chapter 3',
          'https://openstax.org/books/concepts-biology/pages/3-introduction',
        ),
        source(
          'OpenStax · Overview of photosynthesis',
          'https://openstax.org/books/concepts-biology/pages/5-1-overview-of-photosynthesis',
        ),
        source(
          'OpenStax · Cellular respiration',
          'https://openstax.org/books/concepts-biology/pages/4-3-citric-acid-cycle-and-oxidative-phosphorylation',
        ),
      ],
      sections: [
        section('cell', 'A cell is a system with a boundary', [
          {
            kind: 'paragraph',
            text: 'A cell has to obtain materials, use energy, build its components, and dispose of waste. These tasks are linked. A boundary allows the cell to maintain conditions different from its surroundings, but the same boundary must also permit exchanges. To understand a cell, we need both its structures and the processes those structures make possible.',
          },
          {
            kind: 'paragraph',
            text: 'A cell is the smallest basic unit of cellular life: an organized system that carries out chemical reactions while maintaining a boundary. Its cytoplasm is the material inside that boundary, including the fluid in which many reactions occur. DNA stores inherited instructions; proteins perform many of the cell’s structural and chemical jobs. A ribosome builds a protein by joining amino acids in an order specified through those instructions. A compartment is a separated region that can maintain conditions suited to particular reactions. These definitions make cell structures useful explanations rather than a list of names to memorize.',
          },
          {
            kind: 'paragraph',
            text: 'A cell’s plasma membrane encloses its contents and regulates exchange with the surroundings. The basic cellular plan includes a plasma membrane, cytoplasm, genetic material, and ribosomes. Some specialized cells lose components during development; mature mammalian red blood cells are a familiar exception. These common features support the basic tasks of maintaining an internal environment, storing instructions, and making proteins.',
          },
          {
            kind: 'paragraph',
            text: 'Prokaryotic cells, including bacteria, lack a membrane-enclosed nucleus. That does not mean they lack DNA. Eukaryotic cells have a nucleus and other membrane-bound compartments. Plants and animals are both eukaryotes, so a nucleus is not a feature that separates those two groups. Ribosomes are where proteins are assembled; they occur in both prokaryotic and eukaryotic cells.',
          },
          {
            kind: 'paragraph',
            text: 'A plant cell typically has a cell wall outside its plasma membrane. The wall provides support, while the membrane regulates exchange. A wall is therefore not a replacement for a membrane. Many plant cells contain a large central vacuole, which contributes to water balance and internal pressure. Chloroplasts occur in photosynthetic plant tissues, but it would be too strong to claim that every plant cell, including every root cell, contains them.',
          },
        ]),
        section('transport', 'Movement follows gradients unless energy changes the direction', [
          {
            kind: 'paragraph',
            text: 'Concentration is the amount of a substance per volume. Ten dissolved particles in a small volume can be more concentrated than twenty in a much larger volume. A concentration gradient is a change in concentration from one place to another. It describes a difference that can drive a net transfer, not a force that tells every molecule which way to go. A selectively permeable membrane allows some substances to cross more readily than others.',
          },
          {
            kind: 'paragraph',
            text: 'Molecules move randomly because of thermal motion. If a substance can cross a membrane and is more concentrated on one side, random motion produces a net movement toward the less concentrated side. That net movement down a concentration gradient is diffusion. At equilibrium, molecules keep moving in both directions; equal opposing flows mean no net change. Equilibrium is not molecular stillness.',
          },
          {
            kind: 'paragraph',
            text: 'For example, imagine 100 freely diffusing molecules on one side of a permeable boundary and 20 on the other, with equal compartment volumes. If each molecule has the same small chance of crossing in the next moment, more will cross from the crowded side simply because more start there. Individual molecules still move both ways. As the concentrations approach equality, the opposing average transfers approach equality. This explains net diffusion without giving molecules a goal.',
          },
          {
            kind: 'paragraph',
            text: 'Some substances cross through membrane proteins. Facilitated diffusion still moves down the relevant gradient and does not directly require cellular energy to drive that movement. Active transport moves substances against an electrochemical gradient by coupling transport to an energy source. A concentration gradient is part of this story; for charged particles, electrical forces also matter.',
          },
          {
            kind: 'table',
            caption: 'Movement across a membrane',
            headers: ['Process', 'What moves', 'Direction / energy'],
            rows: [
              ['Diffusion', 'Permeable particles', 'Net flow down a gradient'],
              ['Osmosis', 'Water', 'Depends on solute and pressure differences'],
              [
                'Active transport',
                'Transported substances',
                'Against a gradient, coupled to energy',
              ],
            ],
          },
          {
            kind: 'paragraph',
            text: 'Osmosis is the net movement of water across a selectively permeable membrane. In a simple setup where solute cannot cross and pressure differences are initially negligible, water tends to move toward the side with more dissolved solute. An animal cell placed in a sufficiently concentrated external salt solution therefore loses water and shrinks. In a dilute external solution it may gain water and swell. Plant walls can resist swelling and allow pressure to build, so identical water movement need not produce identical changes in cell shape.',
          },
        ]),
        section('carbon', 'Photosynthesis builds organic matter using light', [
          {
            kind: 'paragraph',
            text: 'Matter is made of atoms, and a molecule is a group of bonded atoms. Chemical reactions rearrange those atoms into different molecules. Energy can be transferred during that rearrangement, but supplying energy does not supply the carbon, hydrogen, or oxygen atoms themselves. This is why a plant needs both an energy source and material inputs.',
          },
          {
            kind: 'paragraph',
            text: 'Photosynthesis converts light energy into chemical energy and uses carbon dioxide as a carbon source for building sugars. A simplified overall equation is carbon dioxide + water + light energy → sugar + oxygen. The atoms in the sugar do not come from light: light supplies energy, while matter comes from molecules entering the process.',
          },
          {
            kind: 'equation',
            label: 'Photosynthesis: a process summary',
            lines: [
              {
                left: 'carbon dioxide + water + light energy',
                right: 'sugar + oxygen',
              },
            ],
            note: 'This word equation tracks inputs and outputs. It is not a balanced molecular equation, and light supplies energy rather than atoms.',
          },
          {
            kind: 'paragraph',
            text: 'This distinction helps explain a growing tree. Much of the carbon in its dry organic material ultimately came from carbon dioxide in the air. Soil supplies water and mineral nutrients, but it is not the main source of the carbon skeletons of the tree’s sugars and other organic molecules. Confusing the visible location of roots with the source of all plant mass obscures the role of gas exchange.',
          },
          {
            kind: 'paragraph',
            text: 'Chloroplasts organize the photosynthetic reactions in plants and algae. The broad summary equation is useful for tracking inputs and outputs, but the process involves many steps. Oxygen released by photosynthesis originates from water. The central point here is that energy conversion and matter transformation happen together but are different accounting tasks.',
          },
        ]),
        section('respiration', 'Cells also need a usable supply of energy', [
          {
            kind: 'paragraph',
            text: 'Cells need energy for activities such as building molecules and moving substances against a gradient. A process that releases usable energy can be coupled to a process that requires it, so the combined process can occur. ATP, adenosine triphosphate, is one molecule cells use in this coupling: its conversion to ADP and phosphate can be linked to cellular work. ATP is continually regenerated rather than serving as an unlimited energy supply. Energy released by a reaction depends on the whole change from reactants to products, not merely breaking a bond.',
          },
          {
            kind: 'paragraph',
            text: 'Cellular respiration transfers energy from organic molecules into forms cells can use, including ATP. In the simplified aerobic summary, sugar and oxygen are converted into carbon dioxide and water while energy is transferred. Mitochondria carry out major stages of aerobic respiration in eukaryotic cells; some early steps occur in the cytoplasm.',
          },
          {
            kind: 'paragraph',
            text: 'Plants respire as well as photosynthesize. Photosynthesis supplies organic molecules, while respiration helps make their stored energy available for cellular work. A plant in darkness does not become an organism with no energy needs. It can continue using stored molecules and oxygen for respiration, although sustained darkness eventually limits its resources and growth.',
          },
          {
            kind: 'paragraph',
            text: 'ATP is a short-term energy-transfer molecule, not a magical source that creates energy from nothing. Cells use energy released by favorable processes to support unfavorable ones, such as building complex molecules or moving certain substances against gradients. Some transferred energy becomes thermal energy. Matter is rearranged and energy is transformed; neither should be treated as appearing from nowhere in the classroom model.',
          },
        ]),
        section('connect', 'Use the system to predict an observation', [
          {
            kind: 'paragraph',
            text: 'Suppose a green aquatic plant is enclosed in a transparent container. In strong light, photosynthesis may exceed respiration, so the net effect can be oxygen production and carbon dioxide removal. In darkness, photosynthesis stops while respiration continues, so the net gas changes can reverse. The observed result depends on the balance of simultaneous processes, not merely on whether the organism is labeled a plant.',
          },
          {
            kind: 'paragraph',
            text: 'Or consider a membrane experiment with a large dissolved molecule that cannot cross and water that can. Predicting the initial direction of water movement requires comparing the solutions on both sides. Saying only “water moves into cells” is not a rule; the surrounding conditions determine the direction.',
          },
          {
            kind: 'paragraph',
            text: 'Good biological explanations connect structures, processes, and measurable consequences. Name the boundary or compartment involved, identify what matter crosses or changes, specify where energy comes from, and then predict what you would observe. For the aquatic plant, the measured oxygen change is oxygen produced by photosynthesis minus oxygen consumed in respiration. If the rates are eight and three units per hour, the net release is five. If light decreases production to one while respiration remains three, the same plant takes up two units per hour. Its classification has not changed; the balance of rates has.',
          },
        ]),
      ],
    },
    retiredQuestions: [
      choice(
        'bio-1',
        'A bacterium has no membrane-enclosed nucleus. Which conclusion follows?',
        [
          'It cannot contain DNA.',
          'It cannot make proteins.',
          'Its DNA is not enclosed in a nucleus.',
          'It has no plasma membrane.',
        ],
        2,
        'Prokaryotes have DNA and ribosomes but lack a membrane-enclosed nucleus.',
        ['cell'],
      ),
    ],
    questions: [
      choice(
        'bio-compartments',
        'An unknown cell has DNA, ribosomes, and a plasma membrane. Which additional observation would establish that it is eukaryotic rather than bacterial?',
        [
          'It can use organic molecules for energy.',
          'It has a supporting wall outside its membrane.',
          'Its chromosomes lie inside a membrane-enclosed nucleus.',
          'It makes proteins from amino acids.',
        ],
        2,
        'DNA, ribosomes, protein synthesis, and membranes occur in both groups. Some cells in both groups also have walls. A membrane-enclosed nucleus distinguishes a eukaryotic cell from a bacterium.',
        ['cell'],
      ),
      choice(
        'bio-2',
        'Which pair is expected in both a typical animal cell and a bacterium?',
        [
          'A plasma membrane and ribosomes',
          'Chloroplasts and a plasma membrane',
          'A cell wall and mitochondria',
          'A nucleus and ribosomes',
        ],
        0,
        'Membranes and ribosomes are shared cell features; nuclei and mitochondria are not features of bacteria.',
        ['cell'],
      ),
      choice(
        'bio-3',
        'An animal cell is placed in a salt solution with a higher concentration of nonpenetrating solute than its cytoplasm. Salt cannot cross its membrane on the timescale observed, but water can, and initial pressure differences are negligible. What initially happens?',
        [
          'Water enters and the cell swells.',
          'Salt must enter before any water moves.',
          'Water movement stops immediately.',
          'Water leaves and the cell shrinks.',
        ],
        3,
        'With higher nonpenetrating solute outside and initially negligible pressure difference, water moves outward by osmosis.',
        ['transport'],
      ),
      choice(
        'bio-4',
        'A dissolved substance reaches diffusion equilibrium across a permeable membrane. What is true?',
        [
          'Every molecule stops moving.',
          'Molecules move both ways with no net flow.',
          'Molecules move only toward the cell.',
          'The membrane becomes impermeable.',
        ],
        1,
        'Equilibrium balances opposing movements; it does not stop random molecular motion.',
        ['transport'],
      ),
      choice(
        'bio-5',
        'A cell moves an uncharged solute from a lower concentration to a higher concentration. What does sustained net transport require?',
        [
          'An open channel that permits diffusion',
          'An energy-coupled process',
          'A higher concentration on the receiving side alone',
          'A membrane protein that only facilitates downhill diffusion',
        ],
        1,
        'Transport against a concentration gradient requires coupling to an energy source.',
        ['transport'],
      ),
      choice(
        'bio-6',
        'Where does much of the carbon in a tree’s newly made dry organic material originate?',
        [
          'Mineral particles in soil',
          'Oxygen gas entering leaves',
          'Sunlight converted into atoms',
          'Carbon dioxide entering leaves',
        ],
        3,
        'Photosynthesis fixes carbon from carbon dioxide into organic molecules. Light supplies energy, not carbon atoms.',
        ['carbon'],
      ),
      choice(
        'bio-7',
        'Which statement correctly distinguishes matter from energy in photosynthesis?',
        [
          'Light supplies energy; carbon dioxide supplies carbon.',
          'Light supplies energy; soil minerals supply most sugar carbon.',
          'Carbon dioxide supplies energy; light supplies sugar’s matter.',
          'Water supplies sugar’s carbon; light supplies energy.',
        ],
        0,
        'The carbon skeleton is built from matter; light energy drives the process.',
        ['carbon'],
      ),
      choice(
        'bio-8',
        'What can a living plant cell continue doing during the night if substrates are available?',
        [
          'Running photosynthesis at the same rate as in bright light',
          'Obtaining all needed ATP directly from stored light',
          'Respiring using stored organic molecules',
          'Using oxygen only after its stored sugars run out',
        ],
        2,
        'Plants respire in darkness using stored molecules; they do not only respire when photosynthesis occurs.',
        ['respiration'],
      ),
      choice(
        'bio-9',
        'Why can a green plant show net oxygen release in bright light and net oxygen uptake in darkness?',
        [
          'Mitochondria function at night but become inactive in daylight.',
          'The same photosynthetic reaction reverses every night.',
          'Plants use respiration only when their stomata are closed.',
          'The balance of photosynthesis and respiration changes.',
        ],
        3,
        'Photosynthesis may exceed respiration in light; in darkness respiration can continue while photosynthesis stops.',
        ['carbon', 'respiration', 'connect'],
      ),
      choice(
        'bio-10',
        'Which best explains why a plant cell wall does not replace its plasma membrane?',
        [
          'The wall supports the cell; the membrane regulates exchange.',
          'The wall replaces selective transport with unrestricted diffusion.',
          'The membrane supplies rigid support while the wall selects solutes.',
          'Having a wall means water concentration no longer affects the cell.',
        ],
        0,
        'Support and regulated exchange are different functions carried out by different structures.',
        ['cell', 'transport'],
      ),
    ],
  }),
  exercise({
    id: 'foundations-physics',
    category: 'foundations',
    title: 'Motion, forces, and energy',
    topic: 'Physics · Early high school',
    difficulty: 'Challenging',
    reading: {
      title: 'Motion, force, and energy',
      sources: [
        source(
          'OpenStax · Physics: acceleration',
          'https://openstax.org/books/physics/pages/3-1-acceleration',
        ),
        source(
          'OpenStax · Newton’s second law',
          'https://openstax.org/books/physics/pages/4-3-newtons-second-law-of-motion',
        ),
        source(
          'OpenStax · Work, energy, and power',
          'https://openstax.org/books/physics/pages/9-introduction',
        ),
      ],
      sections: [
        section('motion', 'Separate position, velocity, and acceleration', [
          {
            kind: 'paragraph',
            text: 'A moving object can be described in three complementary ways. Its position and velocity describe what it is doing; forces explain changes in that motion; energy describes transfers between the object and its surroundings. The same event must satisfy all three descriptions. A steadily rising lift is a useful example: its velocity stays constant even while its height and potential energy increase.',
          },
          {
            kind: 'paragraph',
            text: 'Position says where an object is relative to a chosen origin. Displacement is the change in position, including direction. Distance counts the length of the path traveled. A walker who travels three metres east and then three metres west covers six metres but ends with zero displacement. Neither measure is wrong; they answer different questions.',
          },
          {
            kind: 'paragraph',
            text: 'Average velocity is displacement divided by elapsed time, while average speed is distance divided by elapsed time. Velocity includes direction, so motion around a circle can have constant speed and changing velocity. Acceleration means a change in velocity per unit time. It can result from speeding up, slowing down, or changing direction.',
          },
          {
            kind: 'equation',
            label: 'Average rates describe change over time',
            lines: [
              {
                left: 'average velocity',
                right: {
                  numerator: 'displacement',
                  denominator: 'elapsed time',
                },
              },
            ],
            note: 'Use distance for speed; use displacement, including direction, for velocity.',
          },
          {
            kind: 'paragraph',
            text: 'A graph’s slope is change in its vertical quantity divided by change in its horizontal quantity. For position against time, a rise of 6 metres over 3 seconds gives 2 m/s: precisely displacement per time. A steeper slope means more displacement in each second. On a velocity graph, a rise of 6 m/s over 3 seconds gives 2 m/s², meaning velocity changes by 2 m/s each second. A horizontal velocity line has zero slope and therefore zero acceleration, even if the line is well above zero velocity.',
          },
          {
            kind: 'paragraph',
            text: 'Why does area under a velocity graph give displacement? At a constant velocity of 2 m/s for 3 s, displacement is velocity × time = 6 m. On the graph, this is the rectangle’s height times its width. For changing velocity, divide time into short intervals, use a thin rectangle for each, and add their signed areas; progressively narrower intervals give the displacement. Regions below zero velocity subtract because motion there is in the negative direction.',
          },
          {
            kind: 'paragraph',
            text: 'On a position-versus-time graph, the slope represents velocity. A straight sloping line indicates constant velocity, not constant acceleration in the everyday sense of continually speeding up. On a velocity-versus-time graph, slope represents acceleration and signed area represents displacement. Always identify the axes before assigning a physical meaning to a graph’s shape.',
          },
        ]),
        section('force', 'Net force changes velocity', [
          {
            kind: 'paragraph',
            text: 'An inertial frame is a frame of reference in which an object with no net external force continues at constant velocity. The laws here use that frame and assume speeds much smaller than the speed of light. Newton’s second law relates the net external force on an object to its mass and acceleration: F = ma, in the simple constant-mass setting. The word net means the vector sum of all relevant external forces. A 10-newton pull to the right and a 6-newton pull to the left give a 4-newton net force to the right, not a 16-newton net force.',
          },
          {
            kind: 'paragraph',
            text: 'In F = ma, F is net external force, m is mass, and a is acceleration. The equation states a physical relationship: with the same mass, doubling the net force doubles acceleration; under the same net force, doubling mass halves acceleration. Solving for a gives a = F/m. This is not a rule for velocity itself, and it does not say the object must move in the force’s direction. A newton is the force that gives a one-kilogram mass an acceleration of one metre per second squared, so N/kg has exactly the units of acceleration.',
          },
          {
            kind: 'example',
            title: 'Example · Opposing pushes on a cart',
            blocks: [
              {
                kind: 'paragraph',
                text: 'A 6 kg cart is pushed 18 N left and 6 N right. Take right as positive. First combine the forces on this one object: −18 + 6 = −12 N. Then divide by mass; do not divide either force separately and forget the other.',
              },
              {
                kind: 'equation',
                label: 'From net force to acceleration',
                lines: [
                  {
                    left: 'a',
                    right: { numerator: '−12 N', denominator: '6 kg' },
                    reason: 'The signed sum includes both pushes.',
                  },
                ],
                note: 'a = −2 m/s²: the acceleration points left. If the cart initially moves right, this acceleration first slows it down.',
              },
            ],
          },
          {
            kind: 'diagram',
            drawing: 'net-force',
            caption: 'Net force is a signed sum on one object.',
            description:
              'A two-kilogram object has a 10-newton rightward force and a 6-newton leftward force. The net is 4 newtons right, producing 2 metres per second squared rightward acceleration.',
          },
          {
            kind: 'paragraph',
            text: 'For a two-kilogram object under that net force, acceleration is 4/2 = 2 metres per second squared to the right. Each second its rightward velocity changes by two metres per second while that force continues. If it was initially moving left, the same acceleration can first slow it down, bring it momentarily to rest, and then make it move right.',
          },
          {
            kind: 'paragraph',
            text: 'Zero net force means zero acceleration. An object can then remain at rest or continue moving at constant velocity. It does not need a continuing net force to keep moving. Everyday experience often suggests otherwise because friction and drag are present: an engine’s forward force may be needed to balance resistance, producing zero net force during steady motion.',
          },
        ]),
        section('pairs', 'Keep force pairs attached to the correct objects', [
          {
            kind: 'paragraph',
            text: 'Newton’s third law says interacting objects exert equal and opposite forces on one another. If a skater pushes a wall, the wall pushes the skater back with equal force. These two forces act on different objects. They do not cancel when calculating the net force on the skater alone.',
          },
          {
            kind: 'paragraph',
            text: 'By contrast, the downward gravitational force and upward support force on a stationary book both act on the book. They may balance, but they are not each other’s third-law partners. The partner of Earth’s gravitational pull on the book is the book’s gravitational pull on Earth. The partner of the table’s support force on the book is the book’s force on the table.',
          },
          {
            kind: 'paragraph',
            text: 'Mass measures inertia and is expressed in kilograms. Weight is gravitational force and depends on the local gravitational field: W = mg. Taking g as 10 metres per second squared, a three-kilogram object weighs thirty newtons. Moving it to a place with weaker gravity changes its weight while leaving its mass essentially unchanged.',
          },
        ]),
        section('energy', 'Account for transfers rather than inventing a disappearance', [
          {
            kind: 'paragraph',
            text: 'Energy is an accounting quantity that can be transferred between objects and converted between forms. To use conservation, first name the system: for example, a falling object together with Earth. Energy entering or leaving that boundary changes the system’s total; transfers inside the boundary change its distribution. Mechanical energy is only one part of the total, so its decrease does not imply that energy ceased to exist.',
          },
          {
            kind: 'paragraph',
            text: 'For a constant force F acting along a displacement d, transferred mechanical energy is the work W = Fd. A force maintained over twice the distance transfers twice as much energy. Its unit is the joule: one newton acting through one metre. A force opposing motion does negative work on the moving object; a perpendicular force does no work because it has no component along the displacement. For changing forces, add the work over short pieces of the motion.',
          },
          {
            kind: 'paragraph',
            text: 'Two familiar energy equations follow from this idea. To lift mass m slowly near Earth, the supporting force is approximately mg. Moving through height h requires work (mg)h, so the object–Earth system gains potential energy mgh. To speed an initially stationary object to speed v under a constant net force, acceleration is a = F/m. Its average speed during the interval t is v/2, so d = vt/2, while v = at. Combining them gives v² = 2ad. Therefore work Fd = mad = ½mv². This motivates the kinetic-energy formula rather than simply presenting a square to memorize; it applies at ordinary, nonrelativistic speeds.',
          },
          {
            kind: 'equation',
            label: 'Two useful energy relationships',
            lines: [
              {
                left: 'ΔU',
                right: 'mgh',
                reason: 'Lifting near Earth: supporting force mg acts through height h.',
              },
              {
                left: 'K',
                right: '½mv²',
                reason: 'Kinetic energy at ordinary speeds; derived from work during acceleration.',
              },
            ],
          },
          {
            kind: 'paragraph',
            text: 'Kinetic energy is the energy associated with an object’s motion. For an object of mass m moving at speed v at ordinary speeds, K = ½mv². Doubling speed at fixed mass quadruples kinetic energy. Near Earth’s surface, lifting a mass m through a height h increases the gravitational potential energy of the object–Earth system by approximately mgh, where g is the local gravitational acceleration. That expression requires a chosen reference height; differences in potential energy are what matter for the transfer.',
          },
          {
            kind: 'paragraph',
            text: 'Work is a transfer of energy by a force acting through displacement. For a constant force along the displacement, W = Fd. A force perpendicular to the displacement does no work in this model, even though it can change the direction of motion. Holding a heavy bag stationary can feel tiring, but the upward force on the stationary bag does no mechanical work on it because its displacement is zero. Your body’s internal energy use is a different system to analyze.',
          },
          {
            kind: 'paragraph',
            text: 'If an object falls without air resistance, gravitational potential energy decreases while kinetic energy increases. With friction or drag, some mechanical energy becomes thermal energy and other forms. Total energy is conserved for an appropriately isolated system; mechanical energy need not be. Saying energy is “lost to friction” is shorthand for a transfer out of the mechanical account, not destruction of energy.',
          },
        ]),
        section('power', 'Combine the ideas in a physical check', [
          {
            kind: 'paragraph',
            text: 'Power is the rate of energy transfer. If W joules of work are done in t seconds, the average power is P = W/t. Here W denotes work; it is a different quantity from weight, although both are sometimes assigned that letter. Two motors that lift the same mass through the same height do the same ideal lifting work, but the motor that does it in half the time supplies twice the average power. Energy and power therefore have different units and different meanings: joules measure energy; watts measure joules per second.',
          },
          {
            kind: 'equation',
            label: 'Power is energy per unit time',
            lines: [
              {
                left: 'average power',
                right: {
                  numerator: 'energy transferred',
                  denominator: 'elapsed time',
                },
              },
            ],
            note: 'Joules per second are watts. Transferring the same energy faster raises power.',
          },
          {
            kind: 'paragraph',
            text: 'Suppose a lift carries a load upward at constant velocity. Its kinetic energy stays constant, while its gravitational potential energy increases. The upward force can balance weight, so net force and acceleration are zero even though the motor is doing positive work. There is no contradiction: net force governs velocity change, while individual forces can transfer energy between the chosen system and its surroundings.',
          },
          {
            kind: 'paragraph',
            text: 'For example, raise a 20 kg load by 3 m, taking g = 10 m/s². The load gains 20 × 10 × 3 = 600 J of gravitational potential energy. Ignoring losses and assuming equal starting and ending speeds, the motor supplies 600 J. Taking six seconds requires 100 W on average; taking three seconds requires 200 W. The shorter time changes power, while the mass and height fix the required energy.',
          },
        ]),
      ],
    },
    questions: [
      choice(
        'physics-1',
        'A person walks 3 m east, then 3 m west in 6 s. What is their average velocity?',
        ['1 m/s east', '6 m/s west', '0.5 m/s east', '0 m/s'],
        3,
        'Net displacement is zero, so average velocity is zero despite a nonzero distance traveled.',
        ['motion'],
      ),
      choice(
        'physics-2',
        'A car travels around a circular track at constant speed. Is it accelerating?',
        [
          'No, because speed is constant.',
          'Yes, because velocity changes direction.',
          'No, because it returns to its start.',
          'Only if its engine increases power.',
        ],
        1,
        'Acceleration is any change in velocity, including direction.',
        ['motion'],
      ),
      choice(
        'physics-3',
        'A position–time graph is a straight line with constant positive slope. What does it show?',
        [
          'Constant positive velocity',
          'Increasing acceleration',
          'Zero displacement',
          'Constantly increasing speed',
        ],
        0,
        'Slope on a position–time graph is velocity; constant slope means constant velocity.',
        ['motion'],
      ),
      choice(
        'physics-4',
        'A 2 kg object is pulled 10 N right and 6 N left. With no other horizontal forces, what is its acceleration?',
        ['8 m/s² right', '4 m/s² left', '2 m/s² right', '2 m/s² left'],
        2,
        'Net force is 4 N right, giving a = F/m = 2 m/s² right.',
        ['motion', 'force'],
      ),
      choice(
        'physics-5',
        'A moving puck has zero net external force. What follows in an inertial frame?',
        [
          'Its velocity stays constant.',
          'It must stop immediately.',
          'Its speed rises steadily.',
          'Its acceleration equals its velocity.',
        ],
        0,
        'Zero net force means zero acceleration, not necessarily zero velocity.',
        ['motion', 'force'],
      ),
      choice(
        'physics-6',
        'Which is the third-law partner of the table’s upward force on a book?',
        [
          'Earth’s downward force on the book',
          'The table’s weight',
          'The book’s upward force on Earth',
          'The book’s downward force on the table',
        ],
        3,
        'The interaction pair is table-on-book and book-on-table, acting on different objects.',
        ['pairs'],
      ),
      choice(
        'physics-7',
        'A 3 kg object moves to a location where g is 5 m/s². What are its mass and weight there?',
        ['1.5 kg and 15 N', '3 kg and 30 N', '3 kg and 15 N', '15 kg and 3 N'],
        2,
        'Mass remains 3 kg; weight is mg = 15 N.',
        ['pairs'],
      ),
      choice(
        'physics-8',
        'At fixed mass, tripling speed multiplies kinetic energy by what factor?',
        ['3', '9', '6', '1.5'],
        1,
        'Kinetic energy depends on speed squared, so the factor is 3² = 9.',
        ['motion', 'force', 'energy'],
      ),
      choice(
        'physics-9',
        'A sliding object comes to rest through friction on a level surface. In a system including the object and surface, what happens to its initial kinetic energy?',
        [
          'It is transferred mainly to thermal energy.',
          'It ceases to exist.',
          'It all becomes gravitational potential energy.',
          'It remains kinetic energy of the stopped object.',
        ],
        0,
        'Friction transfers mechanical energy into thermal and other forms; total energy is not destroyed.',
        ['motion', 'force', 'energy'],
      ),
      choice(
        'physics-10',
        'Two ideal motors lift the same load from rest to rest through the same height in the same gravitational field. Motor A takes half as long. Compared with B, A does:',
        [
          'Twice the work at the same power',
          'Half the work at twice the power',
          'The same work at twice the average power',
          'The same work at half the average power',
        ],
        2,
        'The change mgh is the same; dividing that work by half the time doubles average power.',
        ['motion', 'force', 'energy', 'power'],
      ),
    ],
  }),
  exercise({
    id: 'foundations-chemistry',
    category: 'foundations',
    title: 'Atoms, bonds, and reactions',
    topic: 'Chemistry · Early high school',
    difficulty: 'Challenging',
    reading: {
      title: 'Atoms, ions, and chemical reactions',
      sources: [
        source(
          'OpenStax · Atomic structure and symbolism',
          'https://openstax.org/books/chemistry-2e/pages/2-3-atomic-structure-and-symbolism',
        ),
        source(
          'OpenStax · Ionic and molecular compounds',
          'https://openstax.org/books/chemistry-2e/pages/2-6-ionic-and-molecular-compounds',
        ),
        source(
          'OpenStax · Writing and balancing chemical equations',
          'https://openstax.org/books/chemistry-2e/pages/4-1-writing-and-balancing-chemical-equations',
        ),
      ],
      sections: [
        section('identity', 'An element is defined by its proton count', [
          {
            kind: 'paragraph',
            text: 'A chemical formula describes a substance’s composition, and a chemical equation describes a change of substances. Reading either one requires keeping track of what stays fixed. In ordinary chemical reactions, atoms are rearranged while each element’s nuclei retain their identity. Electron transfers can change charge without changing the element.',
          },
          {
            kind: 'paragraph',
            text: 'Atoms contain positively charged protons and electrically neutral neutrons in a small nucleus, with negatively charged electrons occupying the surrounding region. The number of protons is the atomic number and defines the element. An atom with six protons is carbon regardless of how many neutrons or electrons it has.',
          },
          {
            kind: 'paragraph',
            text: 'Isotopes of an element have the same proton count but different neutron counts. Carbon-12 and carbon-14 each have six protons; their nuclei contain six and eight neutrons respectively. The mass number counts protons plus neutrons. It is not the same as the average atomic mass printed on a periodic table, which reflects the masses and abundances of isotopes in a sample.',
          },
          {
            kind: 'paragraph',
            text: 'An electrically neutral atom has equal numbers of protons and electrons. An ion has a net charge because the counts differ. Losing an electron makes an atom more positive; gaining one makes it more negative. A sodium atom that loses one electron is still sodium, because its eleven protons have not changed. Changing the nucleus to a different proton count would be a nuclear transformation, not ordinary ion formation.',
          },
        ]),
        section('bonding', 'Electrons connect atoms into substances', [
          {
            kind: 'paragraph',
            text: 'Chemical bonding involves interactions among electrons and nuclei. In a simple covalent-bond picture, atoms share electron density. Water consists of molecules in which oxygen and hydrogen are covalently bonded. The formula H₂O says that each molecule contains two hydrogen atoms for every oxygen atom.',
          },
          {
            kind: 'paragraph',
            text: 'In an ionic substance such as sodium chloride, positive and negative ions attract in an extended arrangement. The formula NaCl gives the simplest ratio of ions in the solid, not necessarily a collection of isolated two-atom molecules. An overall neutral ionic compound must balance total positive and negative charge. Calcium ions with charge +2 combine with chloride ions of charge −1 in a 1:2 ratio, giving CaCl₂.',
          },
          {
            kind: 'example',
            title: 'Example · Build a neutral ionic formula',
            blocks: [
              {
                kind: 'paragraph',
                text: 'Suppose aluminium ions carry charge +3 and oxide ions carry charge −2. One of each would leave +1, so that pair is not neutral. The smallest total charge that both magnitudes can reach is 6: two aluminium ions contribute +6 and three oxide ions contribute −6.',
              },
              {
                kind: 'equation',
                label: 'Balance charge, then write the ratio',
                lines: [{ left: 'total charge', right: '2 × (+3) + 3 × (−2) = 0' }],
                note: 'The smallest neutral ratio is 2:3, so the formula is Al₂O₃. Subscripts count ions in that ratio; they are not the charges of individual ions.',
              },
            ],
          },
          {
            kind: 'paragraph',
            text: 'A compound is chemically combined matter with a characteristic composition. A mixture combines substances without requiring one fixed chemical ratio. Salt dissolved in water is a mixture: its concentration can vary, and the water and dissolved ions can be separated by suitable physical processes. Dissolving is not automatically proof that a new chemical substance has formed.',
          },
        ]),
        section('reaction', 'Reactions rearrange atoms', [
          {
            kind: 'paragraph',
            text: 'An ordinary chemical reaction changes how atoms are connected and distributed among substances. It does not create a fresh supply of atoms or convert one element’s nucleus into another. A balanced equation is an accounting statement: each element must have the same number of atoms before and after.',
          },
          {
            kind: 'paragraph',
            text: 'Consider hydrogen reacting with oxygen to form water. The balanced equation is 2H₂ + O₂ → 2H₂O. On the left there are four hydrogen atoms and two oxygen atoms; on the right there are the same counts. The coefficients describe numbers of molecules, or proportional amounts in moles. A mole is a counting unit, like a dozen but vastly larger: it contains about 6.022 × 10²³ entities. It lets the same atom-count ratios describe laboratory amounts without listing individual molecules. Changing coefficients changes amounts, not the identities of the molecules.',
          },
          {
            kind: 'table',
            caption: 'Balance water formation by conserving atoms',
            headers: ['Stage', 'Hydrogen: left / right', 'Oxygen: left / right'],
            rows: [
              ['H₂ + O₂ → H₂O', '2 / 2', '2 / 1'],
              ['H₂ + O₂ → 2H₂O', '2 / 4', '2 / 2'],
              ['2H₂ + O₂ → 2H₂O', '4 / 4', '2 / 2'],
            ],
          },
          {
            kind: 'paragraph',
            text: 'Changing H₂O to H₂O₂ to make an equation balance would change water into hydrogen peroxide. That is why balancing adjusts coefficients in front of formulas, not subscripts inside them. First identify the actual reactants and products, then find coefficients that conserve every element. For H₂ + O₂ → H₂O, one water molecule on the right has only one oxygen atom while O₂ supplies two on the left. Put 2 before H₂O to give two oxygen atoms on the right. That also gives four hydrogen atoms there, so put 2 before H₂ on the left. Count both elements again: four hydrogen and two oxygen atoms on each side. Adjusting a coefficient multiplies every atom in that formula, which is why the final recount matters. A numerically balanced equation for the wrong substances does not describe the intended reaction.',
          },
        ]),
        section('closed', 'Choose a boundary before checking conservation', [
          {
            kind: 'paragraph',
            text: 'Mass is conserved to the precision relevant for ordinary chemical reactions in a closed system. If a reaction releases a gas from an open beaker, the beaker and its remaining contents can become lighter because matter left that chosen boundary. Include the escaped gas and the accounting is restored. An apparent mass change is often evidence of an incomplete system definition.',
          },
          {
            kind: 'paragraph',
            text: 'For a simple example, suppose 10 grams of one reactant combines completely with 5 grams of another in a sealed apparatus, producing only one product and leaving no reactants. The product mass is 15 grams. The statement depends on the specified complete reaction and the closed apparatus. If reactants remain, their mass belongs in the final total as well.',
          },
          {
            kind: 'paragraph',
            text: 'Atoms are also conserved when one reactant runs out first. From 2H₂ + O₂ → 2H₂O, four molecules of hydrogen and one molecule of oxygen can make only two molecules of water. Two hydrogen molecules remain unused. Having excess of one ingredient cannot compensate for missing atoms of another. This is the idea of a limiting reactant, expressed without requiring a complicated calculation.',
          },
        ]),
        section('changes', 'Distinguish physical state from chemical identity', [
          {
            kind: 'paragraph',
            text: 'Melting ice changes water’s physical state while leaving the molecular formula H₂O unchanged. Breaking water into hydrogen and oxygen changes chemical identity. The fact that a process is dramatic, absorbs energy, or produces bubbles does not by itself settle the distinction: boiling produces water vapor without changing water molecules into a different compound.',
          },
          {
            kind: 'paragraph',
            text: 'A chemical change replaces some substances with others by rearranging atoms and their interactions. To distinguish it from a state change, ask whether the particles’ chemical identities changed, not whether the material looks different. Crushing salt changes grain size without changing the substance. Reacting substances can create different products even when the visible change is subtle.',
          },
          {
            kind: 'paragraph',
            text: 'Chemical reactions can release or absorb energy. Breaking a bond requires energy; forming a bond releases energy. The net reaction depends on both sets of changes. The common phrase “energy stored in bonds” should not be interpreted as meaning that snapping any bond releases energy by itself.',
          },
          {
            kind: 'paragraph',
            text: 'Compare boiling water with splitting it electrically. Boiling separates H₂O molecules from one another into the gas phase; the molecules are still water. Electrolysis produces hydrogen and oxygen, with different molecular formulas, by rearranging bonds. Both processes require energy, so energy input alone does not distinguish a physical change from a chemical reaction. The identities of the substances do.',
          },
        ]),
      ],
    },
    questions: [
      choice(
        'chem-1',
        'An atom has 6 protons, 8 neutrons, and 6 electrons. Which description is correct?',
        [
          'Carbon-12 with charge +2',
          'Carbon-14, neutral',
          'Oxygen-14, neutral',
          'Nitrogen-14 with charge −2',
        ],
        1,
        'Six protons define carbon; 6 + 8 gives mass number 14; equal electrons and protons give zero charge.',
        ['identity'],
      ),
      choice(
        'chem-2',
        'A neutral sodium atom loses one electron. What changes?',
        [
          'It becomes a different element.',
          'Its proton count decreases by one.',
          'Its mass number increases by one.',
          'It becomes a +1 ion of sodium.',
        ],
        3,
        'Electron loss changes charge, while proton count and mass number remain unchanged.',
        ['identity'],
      ),
      choice(
        'chem-3',
        'Two atoms are isotopes of the same element. They must have:',
        [
          'Equal proton counts and different neutron counts',
          'Equal neutron counts and different proton counts',
          'Equal masses and different charges',
          'Different proton counts and equal electron counts',
        ],
        0,
        'Isotopes share element identity but differ in neutron count.',
        ['identity'],
      ),
      choice(
        'chem-4',
        'What neutral formula combines Ca²⁺ and Cl⁻ ions?',
        ['CaCl', 'Ca₂Cl', 'CaCl₂', 'Ca₂Cl₂'],
        2,
        'One +2 calcium ion needs two −1 chloride ions for total charge zero.',
        ['identity', 'bonding'],
      ),
      choice(
        'chem-5',
        'Which is a mixture rather than a pure compound?',
        [
          'A sample consisting only of CO₂',
          'A sample consisting only of H₂O',
          'Salt water of adjustable concentration',
          'A pure crystal of NaCl',
        ],
        2,
        'Salt water can contain different proportions of dissolved salt and water; it has no single fixed compound formula.',
        ['identity', 'bonding'],
      ),
      choice(
        'chem-6',
        'Which coefficients balance H₂ + O₂ → H₂O, in that order?',
        ['2, 1, 2', '1, 1, 1', '1, 2, 1', '2, 2, 1'],
        0,
        '2H₂ + O₂ → 2H₂O conserves four hydrogen and two oxygen atoms.',
        ['bonding', 'reaction'],
      ),
      choice(
        'chem-7',
        'Why can you not balance a water-forming reaction by changing H₂O to H₂O₂?',
        [
          'The ratio of reactants must be changed by subscripts instead.',
          'Changing the subscript changes the substance.',
          'A balanced equation must contain equal numbers of molecules.',
          'A product formula may change only if the reactants are gases.',
        ],
        1,
        'H₂O₂ is hydrogen peroxide, a different substance; balancing changes amounts, not formulas.',
        ['bonding', 'reaction'],
      ),
      choice(
        'chem-8',
        'An open beaker loses mass while a reaction releases gas. Which interpretation is sound?',
        [
          'Chemical energy release necessarily removes the missing atoms.',
          'Mass conservation applies only to reactions involving solids.',
          'The gas has volume but makes no contribution to mass.',
          'Matter may have left the measured system as gas.',
        ],
        3,
        'The beaker is an open system; escaped matter must be included in a conservation account.',
        ['reaction', 'closed'],
      ),
      choice(
        'chem-9',
        'Under 2H₂ + O₂ → 2H₂O, four H₂ molecules and one O₂ molecule react as far as possible. What remains?',
        [
          'Two water molecules and two hydrogen molecules',
          'Four water molecules only',
          'Two water molecules and one oxygen molecule',
          'One water molecule and three oxygen molecules',
        ],
        0,
        'Oxygen limits the reaction to two water molecules, consuming two of the four hydrogen molecules.',
        ['reaction', 'closed'],
      ),
      choice(
        'chem-10',
        'Which statement about bond energy is correct?',
        [
          'Breaking every bond releases energy.',
          'Breaking bonds requires energy; forming bonds releases it.',
          'Forming every bond requires a net energy input.',
          'A reaction’s energy change depends only on bonds broken.',
        ],
        1,
        'Reaction energy reflects the balance of energy required to break bonds and released on forming new ones.',
        ['changes'],
      ),
    ],
  }),
  exercise({
    id: 'foundations-proportion',
    category: 'foundations',
    title: 'Ratios, percentages, and graphs',
    topic: 'Mathematics · Middle school to early high school',
    difficulty: 'Challenging',
    reading: {
      title: 'Ratios, percentages, and scaling',
      sources: [
        source(
          'OpenStax · Prealgebra: ratios and rates',
          'https://openstax.org/books/prealgebra-2e/pages/5-6-ratios-and-rate',
        ),
        source(
          'OpenStax · Percent applications',
          'https://openstax.org/books/prealgebra-2e/pages/6-2-solve-general-applications-of-percent',
        ),
        source(
          'OpenStax · Slope of a line',
          'https://openstax.org/books/elementary-algebra-2e/pages/4-4-understand-slope-of-a-line',
        ),
      ],
      sections: [
        section('ratio', 'A ratio compares quantities in a particular order', [
          {
            kind: 'paragraph',
            text: 'Many numerical mistakes come from comparing the right numbers to the wrong whole. A ratio names two quantities in a particular order. A percentage expresses a quantity relative to a chosen base. A rate compares quantities with different units. We can connect these ideas by tracking what each numerator and denominator represents.',
          },
          {
            kind: 'paragraph',
            text: 'A ratio of two red beads to three blue beads means red:blue = 2:3. There are five total parts, so the red fraction of all beads is 2/5, not 2/3. The fraction 2/3 compares red with blue. Both numbers are meaningful, but only one answers the part-of-the-whole question.',
          },
          {
            kind: 'paragraph',
            text: 'Equivalent ratios multiply both quantities by the same factor. A batch with eight red and twelve blue beads has the same red-to-blue ratio as two and three. To scale a recipe, identify the factor from the original quantity to the desired quantity and apply it to every ingredient that must remain in proportion. Adding the same amount to both quantities does not generally preserve a ratio.',
          },
          {
            kind: 'paragraph',
            text: 'A rate compares quantities with different units, such as kilometres per hour or dollars per kilogram. Dividing to get a unit rate makes offers comparable, provided the units match. A price per 100 grams cannot be compared directly with a price per kilogram without conversion. Units are part of the number’s meaning, not decoration to be added after the arithmetic.',
          },
        ]),
        section('percent', 'A percentage needs a base', [
          {
            kind: 'paragraph',
            text: 'Multiplication by a fraction means taking that share of a quantity: 1/4 of 80 is 80 divided into four equal parts, or 20. A decimal such as 0.25 expresses the same fraction as 25/100. A percentage is another way to write such a ratio, using a denominator of one hundred. To turn p percent into a multiplier, divide p by 100. This is why “of” becomes multiplication rather than addition.',
          },
          {
            kind: 'paragraph',
            text: 'Percent means per hundred. Twenty percent of 80 is 0.20 × 80 = 16. A percentage increase of twenty percent multiplies the original by 1.20, while a decrease of twenty percent multiplies it by 0.80. Naming the original amount—the base—is essential.',
          },
          {
            kind: 'equation',
            label: 'Convert a percentage into a multiplier',
            lines: [
              {
                left: 'new amount',
                right: 'original amount × (1 + p/100)',
              },
            ],
            note: 'For an increase of p percent. For a decrease, use 1 − p/100.',
          },
          {
            kind: 'example',
            title: 'Example · Two price changes with different bases',
            blocks: [
              {
                kind: 'paragraph',
                text: 'A $60 ticket is reduced by 25%, then the reduced price rises by 20%. Keep 75% in the first step: $60 × 0.75 = $45. In the second step the base is $45, so the increase is $9, giving $54.',
              },
              {
                kind: 'equation',
                label: 'Compose the two multipliers',
                lines: [{ left: 'final / original', right: '0.75 × 1.20 = 0.90' }],
                note: 'The final amount is 90% of the original: a 10% net decrease. Subtracting 25% from 20% would miss the change of base.',
              },
            ],
          },
          {
            kind: 'paragraph',
            text: 'Successive percentage changes use successive bases. Raising a price by twenty percent and then reducing the new price by twenty percent multiplies by 1.20 × 0.80 = 0.96. The final price is four percent below the starting price. The changes do not cancel because twenty percent of the increased price is larger than twenty percent of the original.',
          },
          {
            kind: 'paragraph',
            text: 'If a price of 80 is the result of a twenty-percent discount, it equals eighty percent of the original. Divide 80 by 0.80 to recover an original price of 100. Adding twenty percent to the discounted price would give 96, which answers a different question. Reverse a multiplicative change by dividing by its factor, not by applying the opposite-looking percentage.',
          },
        ]),
        section('rates', 'Distinguish relative change from percentage points', [
          {
            kind: 'paragraph',
            text: 'To compare a change with its starting value, subtract to find the change and then divide by the original: relative change = (new − original) / original. Multiplying by 100 expresses that fraction as a percent. The subtraction measures how much was added; the division asks how large that addition was compared with what we started with.',
          },
          {
            kind: 'equation',
            label: 'Relative change',
            lines: [
              {
                left: 'relative change',
                right: {
                  numerator: 'new − original',
                  denominator: 'original',
                },
              },
            ],
            note: 'Multiply by 100 to express the result as a percentage.',
          },
          {
            kind: 'paragraph',
            text: 'If a rate rises from twenty percent to thirty percent, the increase is ten percentage points. Relative to the starting rate, the increase is 10/20 = fifty percent. These descriptions coexist because one subtracts two percentages while the other divides the change by a base.',
          },
          {
            kind: 'paragraph',
            text: 'Combining group percentages also requires attention to group sizes. Suppose a class of ten students has a sixty-percent completion rate and a class of thirty has a twenty-percent rate. The counts are six and six, so twelve out of forty students completed the task: thirty percent overall. Averaging sixty and twenty to get forty percent gives the small class the same weight as the large class.',
          },
          {
            kind: 'paragraph',
            text: 'The general method is to return to counts or weighted totals, combine them, and divide by the combined denominator. For example, travel 60 km at 30 km/h and another 60 km at 60 km/h. The legs take two hours and one hour respectively, so average speed is total distance / total time = 120/3 = 40 km/h, not (30 + 60)/2 = 45. Averaging the two speeds directly would give equal weight to two unequal durations. Decide what is held equal before choosing an averaging rule.',
          },
        ]),
        section('linear', 'A straight line need not be proportional', [
          {
            kind: 'paragraph',
            text: 'In a relationship between two quantities, x names the input and y the corresponding output. A constant is a value that stays fixed while x changes. If one notebook costs 3 dollars with no extra fees, x notebooks cost y = 3x dollars: every extra notebook contributes the same amount and zero notebooks cost zero. The multiplier has units dollars per notebook, so multiplying by notebooks produces dollars.',
          },
          {
            kind: 'paragraph',
            text: 'A proportional relationship has the form y = kx. Its graph passes through the origin, and y/x is constant for nonzero x. Doubling x doubles y. A general linear relationship y = mx + b can include a nonzero starting amount b. Here m is the added output per unit of input—the slope—and b is the output already present at x = 0, the vertical intercept. It has a constant rate of change but is not proportional when b is nonzero.',
          },
          {
            kind: 'table',
            caption: 'A fixed fee changes the scaling',
            headers: ['Distance x (km)', 'Fare y = 2x + 4 ($)', 'Fare / distance'],
            rows: [
              ['0', '4', 'Not defined'],
              ['10', '24', '2.40 per km'],
              ['20', '44', '2.20 per km'],
            ],
          },
          {
            kind: 'paragraph',
            text: 'A taxi fare of 4 dollars plus 2 dollars per kilometre is y = 2x + 4. Ten kilometres cost 24 dollars, and twenty kilometres cost 44 dollars, not twice 24. The fixed starting fee prevents proportional scaling. The slope is 2 dollars per kilometre, while the intercept is 4 dollars. Those units help distinguish a rate from an initial amount.',
          },
          {
            kind: 'paragraph',
            text: 'To find slope from two points, divide the change in y by the change in x. If a tank contains 10 litres at minute zero and 22 litres at minute four, a constant filling rate would be (22 − 10)/4 = 3 litres per minute. Dividing 22 by 4 would incorrectly include the water that was already present at the start.',
          },
        ]),
        section('scaling', 'Track how many dimensions are changing', [
          {
            kind: 'paragraph',
            text: 'If every length of a rectangle is multiplied by three, its area is multiplied by nine because area is length times width. If every length of a box is multiplied by three, its volume is multiplied by twenty-seven because three dimensions contribute a factor of three each. A scale factor for length is not automatically the same factor for area or volume.',
          },
          {
            kind: 'paragraph',
            text: 'Percentages follow the same dimensional logic. Increasing both sides of a square by ten percent multiplies area by 1.10² = 1.21, an increase of twenty-one percent. The extra one percent comes from the product of the two increases. Ignoring it is an approximation, not an exact calculation.',
          },
          {
            kind: 'paragraph',
            text: 'For a concrete check, begin with a square ten metres on each side: its area is 100 m². Increasing each side by ten percent gives eleven metres and an area of 121 m². The increase is 21 m² out of the original 100 m², or 21%. Two ten-percent length changes cannot simply be added to find an exact area change, because area multiplies the two lengths.',
          },
        ]),
      ],
    },
    questions: [
      choice(
        'proportion-1',
        'Red:blue beads = 2:3. What fraction of all beads are red?',
        ['2/5', '2/3', '3/5', '1/2'],
        0,
        'Two red parts out of five total parts gives 2/5.',
        ['ratio'],
      ),
      choice(
        'proportion-2',
        'A recipe uses 150 g of flour for 6 portions. At the same proportions, how much is needed for 10 portions?',
        ['154 g', '900 g', '250 g', '90 g'],
        2,
        'The scale factor is 10/6; 150 × 10/6 = 250 grams.',
        ['ratio'],
      ),
      choice(
        'proportion-3',
        'A price rises 20% and then falls 20% from its new value. Compared with the original, the final price is:',
        ['Unchanged', '4% lower', '4% higher', '20% lower'],
        1,
        '1.20 × 0.80 = 0.96, so the final value is 96% of the original.',
        ['percent'],
      ),
      choice(
        'proportion-4',
        'After a 20% discount, an item costs $80. What was its original price?',
        ['$96', '$64', '$120', '$100'],
        3,
        '80 is 0.80 times the original, so the original is 80/0.80 = 100.',
        ['percent'],
      ),
      choice(
        'proportion-5',
        'A rate increases from 20% to 30%. What is its relative increase?',
        ['10%', '50%', '30%', '150%'],
        1,
        'The change is 10 percentage points, divided by the original 20%: a 50% relative increase.',
        ['percent', 'rates'],
      ),
      choice(
        'proportion-6',
        'In a group of 10, 60% complete a task. In a group of 30, 20% complete it. What is the combined completion rate?',
        ['30%', '40%', '80%', '20%'],
        0,
        'There are 6 + 6 = 12 completions out of 40 people, giving 30%.',
        ['percent', 'rates'],
      ),
      choice(
        'proportion-7',
        'Which relationship is proportional?',
        ['y = 3x + 1', 'y = x²', 'y = 3/x', 'y = 3x'],
        3,
        'A proportional relationship has a constant ratio y/x and passes through the origin.',
        ['linear'],
      ),
      choice(
        'proportion-8',
        'A tank starts with 10 L and holds 22 L after 4 min at a constant fill rate. What is the fill rate?',
        ['5.5 L/min', '8 L/min', '3 L/min', '12 L/min'],
        2,
        'The added volume is 12 L over 4 minutes, giving 3 L/min.',
        ['linear'],
      ),
      choice(
        'proportion-9',
        'Every edge of a box is doubled. By what factor does its volume change?',
        ['2', '4', '8', '6'],
        2,
        'Three dimensions each double, so volume changes by 2³ = 8.',
        ['scaling'],
      ),
      choice(
        'proportion-10',
        'Each side of a square increases by 10%. By what percentage does its area increase?',
        ['21%', '10%', '20%', '100%'],
        0,
        'Area multiplies by 1.1² = 1.21, a 21% increase.',
        ['percent', 'scaling'],
      ),
    ],
  }),
  exercise({
    id: 'foundations-earth',
    category: 'foundations',
    title: 'Seasons, weather, and the water cycle',
    topic: 'Earth science · Middle school to early high school',
    difficulty: 'Challenging',
    reading: {
      title: 'Sunlight, water, and climate',
      sources: [
        source('NASA · What causes the seasons?', 'https://spaceplace.nasa.gov/seasons/en/'),
        source(
          'NASA · Climate and Earth’s energy budget',
          'https://science.nasa.gov/earth/earth-observatory/climate-and-earths-energy-budget/',
        ),
        source(
          'USGS · The water cycle',
          'https://www.usgs.gov/special-topics/water-science-school/science/water-cycle',
        ),
        source(
          'NASA · What is climate change?',
          'https://science.nasa.gov/climate-change/what-is-climate-change/',
        ),
      ],
      sections: [
        section('seasons', 'Tilt changes sunlight’s angle and duration', [
          {
            kind: 'paragraph',
            text: 'Consider why one region has summer while another has winter, and why clouds can form in air that looked clear. Both questions involve energy reaching and leaving places on Earth. Sunlight varies with geometry; water changes state and moves between reservoirs; oceans and land store energy. These mechanisms act over different timescales, from an afternoon shower to a long-term climate pattern.',
          },
          {
            kind: 'paragraph',
            text: 'Rotation is Earth turning about its axis, producing the day–night cycle. Revolution is Earth traveling around the Sun, taking about a year. The axis is an imaginary line through the poles; the orbital plane is the flat reference plane containing the path around the Sun. These are separate motions. A hemisphere is one half of Earth, with the northern and southern hemispheres divided by the equator.',
          },
          {
            kind: 'paragraph',
            text: 'Earth’s rotation axis is tilted by about 23.5 degrees relative to the perpendicular to its orbital plane. Over one orbit the axis points approximately in the same direction in space. As a result, each hemisphere alternately tilts toward and away from the Sun. This changes both the length of daylight and the angle at which sunlight reaches the surface.',
          },
          {
            kind: 'paragraph',
            text: 'Imagine a fixed beam delivering the same energy each second to a flat surface. Face the surface directly toward the beam and the illuminated patch is small. Tilt it and the same beam spreads across a larger patch. Because the incoming energy is shared among more square metres, each square metre receives less. This explains the role of the Sun’s angle without suggesting that a more direct photon carries more energy.',
          },
          {
            kind: 'paragraph',
            text: 'More direct sunlight concentrates incoming energy over a smaller surface area. Longer days provide more time to receive it. Together these effects explain why the hemisphere tilted toward the Sun experiences summer. The opposite hemisphere is tilted away and experiences winter. Earth’s modest annual change in distance from the Sun is not the main cause of these opposite seasons.',
          },
          {
            kind: 'paragraph',
            text: 'At an equinox, neither hemisphere is tilted toward the Sun in the same seasonal sense, and day and night are approximately equal in length across much of Earth. At a solstice, the hemispheric contrast is near its annual extreme. These astronomical events describe geometry; local temperature need not reach its annual maximum on the longest day, because land and oceans store and release energy over time.',
          },
        ]),
        section('budget', 'Temperature depends on an energy balance', [
          {
            kind: 'paragraph',
            text: 'An energy balance compares transfers over the same period: change in stored energy = energy received − energy sent out. Receiving 100 joules while losing 80 adds 20 joules to storage. With no net gain, the stored amount can stay steady even while large transfers continue in both directions. This is a balance of flows, not an absence of activity. How much a body’s temperature changes also depends on how much energy it takes to warm that material.',
          },
          {
            kind: 'diagram',
            drawing: 'energy-flow',
            caption: 'Energy balance compares transfers over the same interval.',
            description:
              'A system receives 100 joules and releases 80 joules during one interval. Its stored energy increases by 20 joules; input and output continue even when their difference is zero.',
          },
          {
            kind: 'paragraph',
            text: 'Earth receives energy mainly as sunlight and emits energy as infrared radiation. Some incoming sunlight is reflected by clouds, ice, land, and other surfaces. Albedo describes the fraction reflected. A brighter surface with higher albedo absorbs a smaller fraction of the incoming sunlight, all else equal.',
          },
          {
            kind: 'paragraph',
            text: 'Greenhouse gases absorb and emit infrared radiation. They influence how energy moves from the surface and atmosphere to space, making the surface warmer than it would be under a comparable atmosphere without that infrared interaction. This is different from the ozone layer’s role in absorbing much incoming ultraviolet radiation. Confusing the greenhouse effect with the ozone hole mixes distinct mechanisms.',
          },
          {
            kind: 'paragraph',
            text: 'If the Earth system absorbs more energy than it emits, energy accumulates and the system tends to warm until changes in emission and other responses alter the balance. The oceans can store a great deal of energy, so the response is not instantaneous. A physical explanation needs both the direction of energy transfer and the capacity of different parts of the system to store it.',
          },
        ]),
        section('phases', 'Water changes phase while exchanging energy', [
          {
            kind: 'paragraph',
            text: 'Evaporation changes liquid water into water vapor, a gas. It can occur below the boiling point because some molecules at the liquid surface have enough energy to escape. Water vapor itself is invisible. A visible cloud consists mainly of tiny liquid droplets or ice crystals, not a visible gas.',
          },
          {
            kind: 'paragraph',
            text: 'Condensation changes water vapor into liquid droplets. When moist air rises, the surrounding pressure usually decreases and the air expands and cools. Saturation is the condition in which water vapor is in equilibrium with liquid water at that temperature. Cooling lowers the vapor pressure associated with saturation, so a moist parcel can reach saturation without gaining water. Further cooling can produce condensation onto small particles. Not every rising parcel necessarily produces rain: droplets or ice particles must grow enough, and conditions below the cloud affect whether precipitation reaches the ground.',
          },
          {
            kind: 'paragraph',
            text: 'Evaporation requires energy, which can cool the surface supplying it. Condensation releases latent heat to the surroundings. “Latent” refers to energy associated with changing phase rather than simply changing temperature. Molecules escaping liquid must overcome attractions to their neighbors; returning to the liquid transfers energy back to the surroundings. Energy absorbed during evaporation can therefore be released elsewhere when the vapor condenses. These transfers help connect the water cycle with atmospheric motion and energy balance. The water is not disappearing when it evaporates; it has changed state and location, carrying energy through the system.',
          },
          {
            kind: 'table',
            caption: 'Water changes state while transferring energy',
            headers: ['Change', 'Direction', 'Energy transfer'],
            rows: [
              ['Evaporation', 'Liquid → vapor', 'Absorbs energy from surroundings'],
              ['Condensation', 'Vapor → liquid', 'Releases energy to surroundings'],
              ['Freezing', 'Liquid → ice', 'Releases energy to surroundings'],
            ],
          },
        ]),
        section('cycle', 'Trace reservoirs and paths', [
          {
            kind: 'paragraph',
            text: 'The water cycle moves water among oceans, atmosphere, land, ice, and living things. Solar energy supports evaporation, and gravity drives precipitation, runoff, and much groundwater movement. Plants return water to the atmosphere through transpiration. Water can enter soil through infiltration and move into underground stores, rather than all rainfall running directly to a river.',
          },
          {
            kind: 'paragraph',
            text: 'Groundwater is generally water occupying pores and fractures beneath the surface, not one vast underground river. Its movement depends on materials and pressure conditions, and some groundwater remains underground for a long time. Reservoirs in the water cycle therefore have different storage amounts and residence times. The phrase “cycle” does not mean every water molecule completes the same route at the same speed.',
          },
          {
            kind: 'paragraph',
            text: 'For a drainage basin, conservation provides a useful account: change in storage equals inputs minus outputs over the chosen period. If rainfall adds 100 units while evaporation and outflow together remove 80, storage rises by 20, assuming those are all the relevant transfers. The units might be volumes or an equivalent depth over the basin, but they must be consistent.',
          },
        ]),
        section('climate', 'Distinguish a day’s outcome from a long-term distribution', [
          {
            kind: 'paragraph',
            text: 'Weather describes conditions over relatively short periods: today’s temperature, rain, wind, or pressure. Climate describes longer-term patterns, including typical values, seasonal cycles, variability, and extremes. A cold day can occur within a warming climate, just as one low score can occur in a group whose average is increasing. One event does not determine the long-term distribution.',
          },
          {
            kind: 'paragraph',
            text: 'Local climate depends on more than latitude. Elevation, proximity to oceans, prevailing winds, currents, and topography all matter. In a common mountain pattern, air rising on a windward slope cools and can lose moisture as precipitation. Air descending on the leeward side warms, which can contribute to drier conditions called a rain shadow. Real geography requires checking the prevailing airflow rather than assuming every mountain behaves identically.',
          },
          {
            kind: 'paragraph',
            text: 'For example, a region with warming winters can still have freezing nights. To evaluate a change in climate, compare many winters using the same measurement: average temperature, the frequency of freezing days, or the timing of seasonal events. A single cold night answers what happened then. The distribution of nights over many years answers whether the region’s pattern has changed.',
          },
        ]),
      ],
    },
    questions: [
      choice(
        'earth-1',
        'Why are Northern and Southern Hemisphere seasons generally opposite?',
        [
          'Earth’s changing Sun distance alternately warms one hemisphere and cools the other.',
          'Seasonal changes in Earth’s rotation speed change the length of daylight.',
          'Earth’s tilt alternates which hemisphere receives longer days and more direct sunlight.',
          'Ocean circulation alone determines which hemisphere has summer.',
        ],
        2,
        'The hemispheres tilt toward and away from the Sun in opposite seasonal phases.',
        ['seasons'],
      ),
      choice(
        'earth-2',
        'Why does more direct sunlight tend to heat a surface more strongly, all else equal?',
        [
          'Direct rays always contain more energy per photon.',
          'The incoming energy is concentrated over a smaller area.',
          'A higher Sun angle changes infrared emission into visible light.',
          'Direct sunlight is reflected less by every possible surface.',
        ],
        1,
        'Sun angle changes the area over which a given incoming beam is spread.',
        ['seasons'],
      ),
      choice(
        'earth-3',
        'A surface becomes more reflective while incoming sunlight stays the same. What initially happens to the sunlight it absorbs?',
        [
          'It increases.',
          'It becomes ultraviolet only.',
          'It must stay identical.',
          'It decreases.',
        ],
        3,
        'Higher albedo reflects a larger fraction and leaves a smaller fraction to be absorbed.',
        ['budget'],
      ),
      choice(
        'earth-4',
        'Which description identifies the greenhouse mechanism?',
        [
          'Certain gases absorb and emit infrared radiation.',
          'Thinning ozone increases the amount of infrared radiation emitted by the Sun.',
          'The atmosphere reflects all outgoing radiation back to the ground.',
          'Greenhouse gases mainly absorb incoming ultraviolet radiation.',
        ],
        0,
        'The greenhouse mechanism involves infrared absorption and emission, distinct from ozone’s ultraviolet role.',
        ['budget'],
      ),
      choice(
        'earth-5',
        'What makes an ordinary cloud visible?',
        [
          'Water vapor molecules in the gas phase',
          'Dry air that becomes opaque when cooled',
          'Tiny water droplets or ice crystals',
          'Rain-sized drops only; smaller droplets remain invisible',
        ],
        2,
        'Water vapor is invisible; droplets and ice particles scatter light.',
        ['phases'],
      ),
      choice(
        'earth-6',
        'Why can rising moist air form a cloud?',
        [
          'It is compressed and heats until water vapor becomes liquid.',
          'Its pressure rises until evaporation necessarily stops.',
          'It gains water because cold air always contains more vapor.',
          'It can expand and cool to saturation.',
        ],
        3,
        'Cooling during expansion can produce saturation and condensation.',
        ['phases'],
      ),
      choice(
        'earth-7',
        'Which phase change absorbs energy from its surroundings?',
        [
          'Condensation',
          'Evaporation',
          'Freezing under ordinary conditions',
          'Water vapor becoming liquid droplets',
        ],
        1,
        'Evaporation requires latent heat; condensation releases it.',
        ['phases'],
      ),
      choice(
        'earth-8',
        'A basin receives 100 units of water and loses 80 in a period, with no other transfers. How does storage change?',
        ['Rises by 20', 'Falls by 20', 'Rises by 180', 'Stays unchanged because water cycles'],
        0,
        'Storage change equals inputs minus outputs: 100 − 80 = 20.',
        ['cycle'],
      ),
      choice(
        'earth-9',
        'Which statement about one unusually cold day is justified?',
        [
          'It establishes a cooling climate if it breaks a daily record.',
          'It establishes a warming climate if last year’s same day was colder.',
          'It has no relation to climate because climate excludes variability.',
          'It can occur within a warmer long-term climate.',
        ],
        3,
        'Weather is a short-term outcome; climate describes longer-term patterns and variability.',
        ['climate'],
      ),
      choice(
        'earth-10',
        'Under the usual rain-shadow pattern, why can the leeward side of a mountain be drier?',
        [
          'Descending air cools and therefore loses moisture before reaching the ground.',
          'Air loses moisture while rising, then descends and warms.',
          'The leeward slope receives less solar energy at every latitude.',
          'Air gains most of its moisture after crossing the mountain summit.',
        ],
        1,
        'Windward ascent can cause cooling and precipitation; leeward descent warms the remaining air.',
        ['climate'],
      ),
    ],
  }),
];
